<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 12px;">
      <h2 class="mb-0">Relatório de NF Recebida</h2>
      <v-btn outlined color="primary" @click="$router.push('/relatorios#financeiro')">
        <v-icon left>mdi-arrow-left</v-icon>
        Voltar ao menu
      </v-btn>
    </div>

    <div class="d-flex flex-wrap align-center mt-4 mb-4" style="gap: 12px;">
      <v-text-field
        v-model="filtros.dataInicio"
        type="date"
        label="Emissão inicial"
        solo
        hide-details
        clearable
        class="c-filtro"
      />

      <v-text-field
        v-model="filtros.dataFim"
        type="date"
        label="Emissão final"
        solo
        hide-details
        clearable
        class="c-filtro"
      />

      <v-autocomplete
        v-model="filtros.status"
        :items="statusOptions"
        label="Status"
        clearable
        solo
        hide-details
        class="c-filtro"
      />

      <v-autocomplete
        v-model="filtros.fornecedor"
        :items="fornecedorOptions"
        label="Fornecedor"
        clearable
        solo
        hide-details
        class="c-filtro"
      />

      <v-btn color="primary" :loading="loading" @click="buscarDados">
        <v-icon left>mdi-refresh</v-icon>
        Recarregar
      </v-btn>

      <v-menu>
        <template v-slot:activator="{ on }">
          <v-btn color="primary" outlined v-on="on" :disabled="loading || dadosFiltrados.length === 0">
            <v-icon left>mdi-file-export-outline</v-icon>
            Exportar
          </v-btn>
        </template>

        <v-list>
          <v-list-item link @click="gerarExcel">
            <v-list-item-title>Excel</v-list-item-title>
          </v-list-item>
          <v-list-item link @click="gerarPdf">
            <v-list-item-title>PDF</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div ref="areaPdf">
      <v-data-table
        :headers="headers"
        :items="dadosFiltrados"
        :loading="loading"
        item-key="numero"
        rows-per-page-text="Linhas por página:"
        page-text="{0}-{1} de {2}"
        class="elevation-1"
      >
        <template v-slot:item.data="{ item }">
          {{ formatarDataPtBr(item.data) }}
        </template>

        <template v-slot:item.valorTotal="{ item }">
          {{ formatarMoedaPtBr(item.valorTotal) }}
        </template>

        <template v-slot:item.impostos="{ item }">
          {{ formatarMoedaPtBr(item.impostos) }}
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import relatorioNFRecebidaService from "@/views/Relatorios/services/relatorioNFRecebidaService";
import {
  exportarExcel,
  exportarPdfTabela,
  formatarDataPtBr,
  formatarMoedaPtBr,
  normalizarTexto
} from "@/views/Relatorios/base/relatorioBaseUtils";
import relatorioPermissionMixin from "@/mixins/relatorioPermissionMixin";

export default {
  name: "RelatorioNFRecebidaView",
  mixins: [relatorioPermissionMixin],
  data() {
    return {
      relatorioSchemas: ["NFRecebida", "FisDocRec", "fisdocrec"],
      relatorioFiltrarPorOwner: false,
      loading: false,
      dados: [],
      filtros: {
        dataInicio: "",
        dataFim: "",
        status: "",
        fornecedor: ""
      },
      headers: [
        { text: "Status", value: "status" },
        { text: "Data", value: "data" },
        { text: "Série", value: "serie" },
        { text: "Número", value: "numero" },
        { text: "CNPJ", value: "cnpj" },
        { text: "Razão Social", value: "razaoSocial" },
        { text: "CFOP", value: "cfop" },
        { text: "Valor Total", value: "valorTotal" },
        { text: "Impostos", value: "impostos" }
      ]
    };
  },
  computed: {
    statusOptions() {
      const unicos = new Set(this.dados.map((item) => normalizarTexto(item.status)).filter(Boolean));
      return Array.from(unicos).sort();
    },
    fornecedorOptions() {
      const unicos = new Set(this.dados.map((item) => normalizarTexto(item.razaoSocial)).filter(Boolean));
      return Array.from(unicos).sort();
    },
    dadosFiltrados() {
      const inicio = this.filtros.dataInicio ? new Date(this.filtros.dataInicio) : null;
      const fim = this.filtros.dataFim ? new Date(this.filtros.dataFim) : null;
      if (fim) fim.setHours(23, 59, 59, 999);

      return this.dados.filter((item) => {
        const dataLinha = item.data ? new Date(item.data) : null;
        const statusOk = !this.filtros.status || normalizarTexto(item.status) === normalizarTexto(this.filtros.status);
        const fornecedorOk =
          !this.filtros.fornecedor ||
          normalizarTexto(item.razaoSocial) === normalizarTexto(this.filtros.fornecedor);

        if (!dataLinha || Number.isNaN(dataLinha.getTime())) {
          return statusOk && fornecedorOk;
        }

        const dataInicioOk = !inicio || dataLinha >= inicio;
        const dataFimOk = !fim || dataLinha <= fim;

        return statusOk && fornecedorOk && dataInicioOk && dataFimOk;
      });
    }
  },
  watch: {
    'filtros.dataInicio'() { this.buscarDados(); },
    'filtros.dataFim'() { this.buscarDados(); }
  },
  methods: {
    formatarDataPtBr,
    formatarMoedaPtBr,
    async buscarDados() {
      this.loading = true;
      try {
        const lista = await relatorioNFRecebidaService.listarNFRecebida(this.filtros);
        this.dados = Array.isArray(lista) ? lista : [];
      } finally {
        this.loading = false;
      }
    },
    gerarExcel() {
      const linhas = this.dadosFiltrados.map((item) => ({
        Status: item.status,
        Data: this.formatarDataPtBr(item.data),
        Série: item.serie,
        Número: item.numero,
        CNPJ: item.cnpj,
        "Razão Social": item.razaoSocial,
        CFOP: item.cfop,
        "Valor Total": this.formatarMoedaPtBr(item.valorTotal),
        ICMS: this.formatarMoedaPtBr(item.icms),
        IPI: this.formatarMoedaPtBr(item.ipi),
        PIS: this.formatarMoedaPtBr(item.pis),
        COFINS: this.formatarMoedaPtBr(item.cofins),
        ISS: this.formatarMoedaPtBr(item.iss),
        "ICMS ST": this.formatarMoedaPtBr(item.icmsSt),
        "Outros Impostos": this.formatarMoedaPtBr(item.outrosImpostos),
        "Impostos (Total)": this.formatarMoedaPtBr(item.impostos)
      }));

      exportarExcel(linhas, "relatorio-nf-recebida.xlsx");
    },
    async gerarPdf() {
      const headersPdf = [
        { text: "Status", value: "status" },
        { text: "Data", value: "data" },
        { text: "Série", value: "serie" },
        { text: "Número", value: "numero" },
        { text: "CNPJ", value: "cnpj" },
        { text: "Razão Social", value: "razaoSocial" },
        { text: "CFOP", value: "cfop" },
        { text: "Valor Total", value: "valorTotal" },
        { text: "ICMS", value: "icms" },
        { text: "IPI", value: "ipi" },
        { text: "PIS", value: "pis" },
        { text: "COFINS", value: "cofins" },
        { text: "ISS", value: "iss" },
        { text: "ICMS ST", value: "icmsSt" },
        { text: "Outros Impostos", value: "outrosImpostos" },
        { text: "Impostos (Total)", value: "impostos" }
      ];

      await exportarPdfTabela({
        headers: headersPdf,
        rows: this.dadosFiltrados,
        nomeArquivo: "relatorio-nf-recebida.pdf",
        tituloRelatorio: "Relatório de NF Recebida",
        formatadoresPorCampo: {
          data: (valor) => this.formatarDataPtBr(valor),
          valorTotal: (valor) => this.formatarMoedaPtBr(valor),
          icms: (valor) => this.formatarMoedaPtBr(valor),
          ipi: (valor) => this.formatarMoedaPtBr(valor),
          pis: (valor) => this.formatarMoedaPtBr(valor),
          cofins: (valor) => this.formatarMoedaPtBr(valor),
          iss: (valor) => this.formatarMoedaPtBr(valor),
          icmsSt: (valor) => this.formatarMoedaPtBr(valor),
          outrosImpostos: (valor) => this.formatarMoedaPtBr(valor),
          impostos: (valor) => this.formatarMoedaPtBr(valor)
        }
      });
    }
  },
  async mounted() {
    if (this.relatorioTemPermissao(this.relatorioSchemas)) {
      await this.buscarDados();
    }
  }
};
</script>

<style scoped>
.c-filtro {
  min-width: 220px;
  max-width: 280px;
}
</style>
